/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import liff from "@line/liff";

// @mui material components
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import CssBaseline from "@mui/material/CssBaseline";
import Toolbar from "@mui/material/Toolbar";
import { styled, alpha } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemText from "@mui/material/ListItemText";
import ListItemButton from "@mui/material/ListItemButton";
import Avatar from "@mui/material/Avatar";
import Icon from "@mui/material/Icon";
import Divider from "@mui/material/Divider";
import InputBase from "@mui/material/InputBase";
import DialogTitle from "@mui/material/DialogTitle";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import Skeleton from "@mui/material/Skeleton";
import { blue } from "@mui/material/colors";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";

// Service
import Meeting from "layouts/meeting/Meeting";
import CustomerAdd from "layouts/customer/CustomerAdd";
import i18n from "components/Translation/Translate";
import * as Defi from "services/Define";
import * as Func from "services/Function";
import { FetchData } from "services/Request";
import { setToken, setUser, setLang } from "redux/actions/appAction";
import Task from "layouts/task/Task";
import CustomerEdit from "layouts/customer/CustomerEdit";
import ProfileEdit from "layouts/profile/ProfileEdit";
import TaskAdd from "layouts/task/TaskAdd";
import MeetingAdd from "layouts/meeting/MeetingAdd";
import CustomerUpload from "layouts/customer/CustomerUpload";

const Search = styled("div")(({ theme }) => ({
  position: "relative",
  borderRadius: 20,
  backgroundColor: alpha(theme.palette.common.white, 0.25),
  "&:hover": {
    backgroundColor: alpha(theme.palette.common.white, 0.25),
  },
  marginLeft: 0,
}));

const SearchIconWrapper = styled("div")(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: "100%",
  position: "absolute",
  pointerEvents: "none",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: "inherit",
  width: "100%",
  "& .MuiInputBase-input": {
    padding: theme.spacing(1, 1, 1, 0),
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create("width"),
    [theme.breakpoints.up("xs")]: {
      width: "12ch",
      "&:focus": {
        width: "20ch",
      },
    },
  },
}));

const initAttr = {
  userId: "",
  displayName: "",
  pictureUrl: "",
  statusMessage: "",
  idToken: "",
  email: "",

  acc_no: "",
  firstname: "",
  lastname: "",
  tel: "",
  lineid: "",
  pass: "",
  pass_check: "",
  ref_code: "",
  bank: "",
  captcha: "",
  upline: "",
  upline_name: "",
};

const initCustomer = {
  id: "",
  name: "",
  image: "",
};

const initNotify = {
  open: false,
  title: "",
  color: "info",
};

function Home() {
  const app = useSelector((state) => state[Defi.APP_STATE]);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [attr, setAttr] = useState(initAttr);
  const [loading, setLoading] = useState(true);
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [notify, setNotify] = useState(initNotify);
  const [customer, setCustomer] = useState(initCustomer);
  const [search, setSearch] = useState("");

  const view = () => {
    setLoading(true);

    FetchData("POST", `/customer`, app.token, {
      lang: app.lang,
      search: search,
    }).then((result) => {
      if (result.status === "ok") {
        setList(result.data);
        setLoading(false);
      } else {
        Func.exitApp();
      }
    });
  };

  const upload = (filedata) => {
    setLoading(true);
    setOpen(false);

    FetchData("OPTIONS", `/customer/upload`, app.token, {
      id: customer.id,
      filedata: filedata,
    }).then((result) => {
      setLoading(false);

      if (result.status === "ok") {
        view();
        setNotify({ open: true, title: t(`alert.addcomplete`), color: "success" });
      } else {
        setNotify({ open: true, title: t(`alert.saveerror`), color: "error" });
      }
    });
  };

  const authen = (item) => {
    setLoading(true);
    setAttr({
      ...attr,
      type: item.type,
      userId: item.userId,
      displayName: item.displayName,
      pictureUrl: item.pictureUrl,
      statusMessage: item.statusMessage,
      idToken: item.idToken,
      email: item.email,
    });

    FetchData("POST", `/authen`, app.token, {
      lang: app.lang,
      data: {
        type: item.type,
        token: item.idToken,
        userid: item.userId,
        name: item.displayName,
        filepic: item.pictureUrl,
        status: item.statusMessage,
        email: item.email,
      },
    }).then((result) => {
      if (result.status === "ok") {
        setList(result.customer);
        setLoading(false);
      } else if (result.status === "login") {
        dispatch(setToken(result.token));
        dispatch(setUser(result.user));
        dispatch(setLang(result.user.language));

        setList(result.customer);
        setLoading(false);
      } else {
        liff.closeWindow();
      }
    });
  };

  const loginDebug = () => {
    authen({
      ...attr,
      userId: Defi.DEBUG_ID,
      displayName: Defi.DEBUG_NAME,
      pictureUrl: Defi.DEBUG_PIC,
      statusMessage: Defi.DEBUG_STATUS,
      idToken: Defi.DEBUG_TOKEN,
      email: Defi.DEBUG_EMAIL,
    });
  };

  const lineLogin = () => {
    liff.init(
      { liffId: Defi.LIFF_ID },
      () => {
        if (liff.isLoggedIn()) {
          const idToken = liff.getIDToken();

          liff
            .getProfile()
            .then((profile) => {
              authen({
                ...profile,
                idToken: idToken,
                email: liff.getDecodedIDToken().email,
              });
            })
            .catch((err) => console.error(err));
        } else {
          liff.login();
        }
      },
      (err) => console.error(err)
    );
  };

  useEffect(() => {
    i18n.changeLanguage(app.lang);

    loginDebug();
    // if (window.location.hostname === "localhost") {
    //   loginDebug();
    // } else {
    //   lineLogin();
    // }
  }, []);

  const renderAppBar = () => {
    return (
      <AppBar position="fixed" color="info" sx={{ mb: 3 }}>
        <Toolbar>
          <ProfileEdit callbackNotify={setNotify} />
          <Typography
            variant="h6"
            noWrap
            component="div"
            sx={{ flexGrow: 1, display: { xs: "flex", sm: "block" }, ml: 1 }}
            className="color-light"
          >
            {app.user.name}
          </Typography>
          <MDBox
            noValidate
            component="form"
            role="form"
            onSubmit={(e) => {
              e.preventDefault();
              view();
            }}
          >
            <Search>
              <SearchIconWrapper>
                <Icon>search</Icon>
              </SearchIconWrapper>
              <StyledInputBase
                placeholder={t(`element.search`)}
                inputProps={{ "aria-label": "search" }}
                onChange={(e) => setSearch(e.target.value)}
              />
            </Search>
          </MDBox>
        </Toolbar>
      </AppBar>
    );
  };

  const renderBottomBar = () => {
    return (
      <AppBar position="fixed" color="info" sx={{ top: "auto", bottom: 0 }}>
        <Toolbar>
          <Task />
          <CustomerAdd callbackNotify={setNotify} callbackView={view} />
          <Box sx={{ flexGrow: 1 }} />
          <Meeting />
        </Toolbar>
      </AppBar>
    );
  };

  const renderDialog = () => {
    return (
      <Dialog onClose={() => setOpen(false)} open={open}>
        <DialogTitle>{customer.name}</DialogTitle>
        <DialogContent>
          <List sx={{ pt: 0 }}>
            <TaskAdd parent={customer} callbackNotify={setNotify} callbackOpen={setOpen} />
            <MeetingAdd parent={customer} callbackNotify={setNotify} callbackOpen={setOpen} />
            <CustomerEdit
              parent={customer}
              callbackNotify={setNotify}
              callbackOpen={setOpen}
              callbackView={view}
            />
            <ListItem disableGutters>
              <ListItemButton>
                <ListItemAvatar>
                  <Avatar sx={{ bgcolor: blue[100], color: blue[600] }}>
                    <Icon>image</Icon>
                  </Avatar>
                </ListItemAvatar>
                <CustomerUpload uploadData={upload} />
              </ListItemButton>
            </ListItem>
          </List>
        </DialogContent>
      </Dialog>
    );
  };

  const renderSnackbar = () => {
    return (
      <Snackbar
        open={notify.open}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        autoHideDuration={3000}
        onClose={() => setNotify(initNotify)}
      >
        <Alert
          onClose={() => setNotify(initNotify)}
          severity={notify.color}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {notify.title}
        </Alert>
      </Snackbar>
    );
  };

  const renderList = () => {
    return (
      <MDBox p={2} mt={7}>
        {loading ? (
          <List sx={{ mb: 2 }}>
            <ListItem>
              <ListItemAvatar>
                <Skeleton animation="wave" variant="circular" width={40} height={40} />
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Skeleton animation="wave" height={10} width="90%" style={{ marginBottom: 6 }} />
                }
                secondary={
                  <Skeleton animation="wave" height={10} width="90%" style={{ marginBottom: 6 }} />
                }
              />
            </ListItem>
            <Divider />
          </List>
        ) : (
          <List sx={{ mb: 2 }}>
            {list.map((item) => (
              <div key={item.id}>
                <ListItemButton
                  onClick={() => {
                    setCustomer(item);
                    setOpen(true);
                  }}
                >
                  <ListItemAvatar>
                    <Avatar alt={item.name} src={item.image} />
                  </ListItemAvatar>
                  <ListItemText
                    primary={item.name}
                    secondary={`${item.district_name} ${item.province_name}`}
                  />
                </ListItemButton>
                <Divider />
              </div>
            ))}
          </List>
        )}
      </MDBox>
    );
  };

  return (
    <div>
      <CssBaseline />
      {renderAppBar()}
      {renderList()}
      {renderSnackbar()}
      {renderDialog()}
      {renderBottomBar()}
    </div>
  );
}

export default Home;
